import constant from "../../constant";
import router from "../../router";
import Vue from "vue";
import { authStatus } from "../../plugins/authStatus";

const api = constant.api + "";
const url = constant.url + "";

function safeJsonParse(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return value;
  }
}

function getStoredUser() {
  const user = safeJsonParse(localStorage.getItem(constant.authUser));
  return user && typeof user === "object" ? user : {};
}

function getStoredToken() {
  const token = localStorage.getItem(constant.authToken);
  return token && token !== "null" && token !== "undefined" ? token : null;
}

function cleanToken(token) {
  return typeof token === "string" ? token.replace(/^"|"$/g, "") : String(token);
}

function looksLikePhone(value) {
  return /^[0-9+\s-]+$/.test((value || "").trim());
}

function resolveHttp(store) {
  if (store.$api && store.$api.instance) return store.$api.instance;
  if (store._vm && store._vm.$api && store._vm.$api.instance) {
    return store._vm.$api.instance;
  }
  if (Vue.prototype.$api && Vue.prototype.$api.instance) {
    return Vue.prototype.$api.instance;
  }
  return null;
}

function addDeviceContext(payload, data) {
  if (data.ip_address) {
    payload.ip_address = data.ip_address;
  }

  if (data.device_info && Object.keys(data.device_info).length > 0) {
    payload.device_info = data.device_info;
  }

  return payload;
}

function makeLoginError(message, loginStatus) {
  return Object.assign(new Error(message || "Login failed"), { loginStatus });
}

async function navigateHome() {
  await new Promise((resolve) => {
    Vue.nextTick(() => {
      setTimeout(resolve, 150);
    });
  });

  try {
    await router.push({ name: "home" });
  } catch (error) {
    if (error.name !== "NavigationDuplicated") {
      console.error("Router navigation error:", error);
      window.location.href = "/";
    }
  }
}

const state = () => ({
  authenticated: Boolean(getStoredToken()),
  user: getStoredUser(),
});

const getters = {
  authenticated(state) {
    const token = getStoredToken();
    const user = getStoredUser();

    state.user = user;
    state.authenticated = Boolean(token);

    return state.authenticated;
  },

  user: (state) => {
    const user = getStoredUser();
    state.user = user;
    return state.user;
  },

  getAuthData: () => getStoredToken(),
};

const actions = {
  async login({ commit }, data = {}) {
    const http = resolveHttp(this);

    if (!http) {
      throw new Error("API instance not available. Please refresh the page.");
    }

    const identifier = String(data.username || data.phone || "").trim();

    if (!identifier) {
      throw new Error("Le nom d'utilisateur ou telephone est requis.");
    }

    if (!data.password && !data.token) {
      throw new Error("Le mot de passe est requis.");
    }

    try {
      try {
        await http.get(`${url}sanctum/csrf-cookie`, { timeout: 5000 });
      } catch (csrfError) {
        console.warn("CSRF cookie request failed:", csrfError.message);
      }

      let response = await http.post(
        `${api}auth/login`,
        addDeviceContext(
          {
            username: identifier,
            password: data.password,
            token: data.token,
          },
          data
        ),
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      let responseData = response.data || {};

      if (responseData.status === "usernameError" && looksLikePhone(identifier)) {
        response = await http.post(
          `${api}auth/login/seller`,
          addDeviceContext(
            {
              phone: identifier,
              password: data.password,
            },
            data
          ),
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );
        responseData = response.data || {};
      }

      const { status, message, user, token, roleFullName } = responseData;

      if (status === "confirmationCode") {
        throw makeLoginError(
          "Ce compte requiert un code de confirmation. Connectez-vous depuis l'application principale.",
          status
        );
      }

      if (status !== "success") {
        throw makeLoginError(message || "Identifiants incorrects.", status);
      }

      if (!token || !user) {
        throw new Error("Invalid authentication response from server.");
      }

      commit("setToken", token);
      commit("setUser", user);
      commit("setUserRole", roleFullName || (user.role && user.role.fullName) || "");
      commit("setAuthentificated", true);

      if (authStatus && typeof authStatus.refresh === "function") {
        authStatus.refresh();
      }

      await navigateHome();

      return response;
    } catch (error) {
      const responseMessage =
        (error.response &&
          error.response.data &&
          (error.response.data.message || error.response.data.error)) ||
        error.message;

      console.error("Login error:", {
        message: responseMessage,
        status: error.response && error.response.status,
        data: error.response && error.response.data,
      });

      throw Object.assign(error, {
        message: responseMessage || "Login failed. Please try again.",
      });
    }
  },

  logout({ commit }) {
    commit("setAuthentificated", false);
    commit("setUser", {});
    commit("setUserRole", "");
    localStorage.removeItem(constant.authUser);
    localStorage.removeItem(constant.authToken);
    localStorage.removeItem(constant.userRoleKey);

    if (authStatus && typeof authStatus.refresh === "function") {
      authStatus.refresh();
    }

    if (router.currentRoute.name !== "login") {
      router.push({ name: "login" });
    }
  },
};

const mutations = {
  setAuthentificated(state, value) {
    state.authenticated = value;
  },

  setUser(state, value) {
    state.user = value || {};

    if (value && Object.keys(value).length > 0) {
      localStorage.setItem(constant.authUser, JSON.stringify(value));
    } else {
      localStorage.removeItem(constant.authUser);
    }
  },

  setToken(state, value) {
    localStorage.setItem(constant.authToken, cleanToken(value));
  },

  setUserRole(state, value) {
    if (value) {
      localStorage.setItem(constant.userRoleKey, value);
    } else {
      localStorage.removeItem(constant.userRoleKey);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
